<template>
    <li class="internal-note-wrapper">
        <div
            class="internal-note-banner"
            :class="{ 'is-evento': isEvento, 'is-campanha': ehCampanhaClicavel, 'is-agente-ia': message.noteTipo === 'AGENTE_IA', 'is-falha': message.noteTipo === 'BOT_FALHOU' }"
            :role="ehCampanhaClicavel ? 'button' : undefined"
            :tabindex="ehCampanhaClicavel ? 0 : undefined"
            @click="ehCampanhaClicavel && abrirCampanha()"
            @keydown.enter.prevent="ehCampanhaClicavel && abrirCampanha()"
            @keydown.space.prevent="ehCampanhaClicavel && abrirCampanha()"
        >
            <p class="internal-note-lead">
                <i :class="icone"></i>
                <template v-if="isEvento">{{ fraseEvento }}</template>
                <template v-else>
                    <span class="internal-note-author">{{ autorExibido }}</span>
                    deixou uma anotação interna
                </template>
            </p>

            <p v-if="!isEvento" class="internal-note-text">{{ message.text }}</p>

            <p v-if="ehCampanhaClicavel" class="internal-note-cta">
                Ver na campanha <i class="ri-arrow-right-up-line"></i>
            </p>

            <div class="internal-note-meta">
                <span>{{ horaFormatada }}</span>
                <template v-if="message.noteEdited">
                    <span class="internal-note-sep">·</span>
                    <span class="internal-note-edited">editada</span>
                </template>
                <span class="internal-note-sep">·</span>
                <span class="internal-note-hint">
                    <i class="ri-eye-off-line"></i>
                    Visível apenas para a equipe
                </span>
            </div>

            <DropdownField v-if="isAuthor" class="internal-note-actions" placement="bottom-end" :distance="6">
                <template #default>
                    <button type="button" class="internal-note-menu-btn" title="Ações da anotação">
                        <i class="ri-more-2-fill"></i>
                    </button>
                </template>

                <template #popper="{ hide }">
                    <div class="internal-note-menu">
                        <button type="button" class="internal-note-menu-item" @click="onEdit(hide)">
                            <i class="ri-pencil-line"></i> Editar
                        </button>
                        <button type="button" class="internal-note-menu-item danger" @click="onDelete(hide)">
                            <i class="ri-delete-bin-line"></i> Excluir
                        </button>
                    </div>
                </template>
            </DropdownField>
        </div>
    </li>
</template>

<script lang="ts">
import { defineComponent, PropType, computed } from 'vue';
import { Message } from '@/mocks/app/mockChatData';
import { useChatStore } from '@/stores/app/chatStore';
import { useAuthStoreUser } from '@/stores/app/authUser';
import { AreYouSureAboutThat } from '@/swal/swal-presets';
import { FormatTime } from '@/stores/app/chatUtils';
import { useUiStore } from '@/stores/app/uiStore';
import { formatarDuracaoFalada } from '@/utils/duracao';
import { fraseRodizio } from '@/utils/rodizio/rodizio-frase';

export default defineComponent({
    name: 'ChatInternalNote',
    props: {
        message: { type: Object as PropType<Message>, required: true },
    },
    setup(props) {
        const chatStore = useChatStore();
        const userAuthStore = useAuthStoreUser();
        const uiStore = useUiStore();

        // Evento de sistema é imutável: o backend responde 403 em PATCH/DELETE
        // mesmo para o ator, então o menu de ações não existe aqui.
        const isEvento = computed(() => !!props.message.noteTipo && props.message.noteTipo !== 'MANUAL');

        // Só o autor edita/exclui — mesma regra do backend, que responde 403 para
        // qualquer outro usuário (inclusive acesso global).
        const isAuthor = computed(() => !isEvento.value && !!props.message.idUsuarioSender && props.message.idUsuarioSender === userAuthStore.user?.id);

        /**
         * Ícone por família de evento do Agente. Evento desconhecido (marcador novo criado no
         * bot-ia) cai no robô — genérico, mas correto: foi o Agente que fez.
         */
        function iconeAgenteIa(): string {
            const evento = String((props.message.noteMetadados as { evento?: string } | null)?.evento ?? '');
            if (evento === 'ferramenta_usada') return 'ri-tools-line';
            if (evento.startsWith('escopo_')) return 'ri-shield-check-line';
            if (evento.startsWith('midia_')) return 'ri-image-line';
            if (evento === 'agente_finalizou') return 'ri-checkbox-circle-line';
            if (evento === 'agente_transferiu' || evento === 'transferido_para_canal'
                || evento === 'transferido_por_mensagem_manual') return 'ri-user-shared-line';
            if (evento === 'agente_assumiu') return 'ri-play-circle-line';
            // Follow-up é a única mensagem que sai sem o cliente ter falado — o sino é o que
            // distingue "nós cobramos" de "o Agente respondeu" na varredura da timeline.
            if (evento === 'followup_enviado') return 'ri-notification-3-line';
            if (evento === 'comando_stop' || evento === 'humano_ja_atendendo') return 'ri-pause-circle-line';
            return 'ri-shining-2-line';
        }

        const icone = computed(() => {
            switch (props.message.noteTipo) {
                case 'ATENDIMENTO_INICIADO':    return 'ri-play-circle-line';
                case 'ATENDIMENTO_FINALIZADO':  return 'ri-checkbox-circle-line';
                case 'ATENDIMENTO_TRANSFERIDO': return 'ri-user-shared-line';
                case 'BOT_DISPARADO':           return 'ri-robot-line';
                // Mesmo ícone do badge de campanha no card da lista: é o que liga
                // visualmente a anotação, o badge e o modal como uma coisa só.
                case 'DISPARO_ENVIADO':         return 'ri-megaphone-fill';
                // Ícone conta o desfecho antes da frase: perdida/recusada é
                // telefone cortado, atendida é telefone normal.
                case 'LIGACAO':                 return ligacaoSemFala.value ? 'ri-phone-off-line' : 'ri-phone-line';
                // O ícone do Agente varia por EVENTO, não por tipo: todos os fatos do bot-ia
                // chegam como AGENTE_IA, e um ícone único faria "consultou a planilha" e
                // "transferiu o atendimento" parecerem a mesma coisa na timeline.
                case 'AGENTE_IA':               return iconeAgenteIa();
                // Ícone por RESULTADO, não por tipo: "houve" e "não houve" chegam no
                // mesmo tipo, e um ícone único faria a timeline mostrar conversão e
                // não-conversão como o mesmo fato.
                case 'CONVERSAO':
                    return props.message.noteMetadados?.houve
                        ? 'ri-hand-coin-line'
                        : 'ri-close-circle-line';
                // Setas trocando: o fato é uma substituição de identidade, não
                // um passo do ciclo do atendimento.
                case 'CONTATO_ALTEROU_NUMERO':  return 'ri-arrow-left-right-line';
                // Fallback ganha o triângulo: é a execução que alguém vai querer achar
                // ao perguntar "por que este atendimento caiu para quem estava fora?".
                case 'RODIZIO':
                    return props.message.noteMetadados?.fallback ? 'ri-error-warning-line' : 'ri-shuffle-line';
                case 'BOT_FALHOU':              return 'ri-error-warning-line';
                default:                        return 'ri-sticky-note-line';
            }
        });

        /**
         * Frase montada de `noteTipo` + `noteMetadados`, e não lida do `text`:
         * o servidor congela uma frase em `conteudo` para exportação, mas aqui a
         * redação pode mudar sem migration. `text` fica como fallback se um tipo
         * novo chegar de um servidor mais recente que este front.
         */
        const fraseEvento = computed(() => {
            const meta = props.message.noteMetadados ?? {};
            const ator = props.message.noteAuthorName;

            switch (props.message.noteTipo) {
                case 'ATENDIMENTO_INICIADO':
                    if (!ator) return 'Atendimento iniciado automaticamente';
                    return meta.automatico ? `Atendimento atribuído automaticamente a ${ator}` : `${ator} iniciou o atendimento`;

                case 'ATENDIMENTO_FINALIZADO':
                    return ator ? `${ator} finalizou o atendimento` : 'Atendimento finalizado automaticamente';

                case 'ATENDIMENTO_TRANSFERIDO': {
                    const quem = ator || 'O sistema';
                    if (meta.removeuAtendente) return `${quem} removeu o atendente do atendimento`;
                    if (meta.nomeUsuarioDestino) return `${quem} transferiu o atendimento para ${meta.nomeUsuarioDestino}`;
                    // Canal por último, na mesma ordem do servidor
                    // (`montarConteudoEvento`): destino humano vence canal quando a
                    // ação do bot traz os dois. Linha antiga não tem `nomeCanal`.
                    if (meta.nomeCanal) return `${quem} transferiu o atendimento para o canal ${meta.nomeCanal}`;
                    return `${quem} transferiu o atendimento`;
                }

                case 'BOT_DISPARADO':
                    if (ator) return meta.nomeBot ? `${ator} disparou o bot ${meta.nomeBot}` : `${ator} disparou o bot`;
                    return meta.nomeBot ? `Bot ${meta.nomeBot} disparado automaticamente` : 'Bot disparado automaticamente';

                // O tipo fala DISPARO (nome interno do servidor), a frase fala campanha.
                case 'DISPARO_ENVIADO': {
                    const campanha = meta.nomeDisparo;
                    if (meta.criouAtendimento) {
                        return campanha ? `Atendimento aberto pela campanha "${campanha}"` : 'Atendimento aberto por uma campanha';
                    }
                    return campanha ? `Campanha "${campanha}" enviada neste atendimento` : 'Campanha enviada neste atendimento';
                }

                // Espelha `montarConversao` do servidor. Só houve/não houve: o valor
                // não vem nos metadados de propósito (dado de negócio numa linha que
                // todo usuário do inbox lê), ele mora no relatório e no card. Sem ator
                // porque a linha de finalização vem logo acima — o servidor garante a
                // ordem gravando esta 1 ms depois daquela.
                case 'CONVERSAO':
                    return meta.houve ? 'Conversão: houve' : 'Conversão: não houve';
                // Sem ator: quem trocou o número foi o cliente, no aplicativo
                // dele — a Meta só nos avisou.
                //
                // A ressalva do cadastro está nas DUAS redações porque a troca
                // nunca é automática: `Contatos.codigo` é identidade sem unique
                // no banco, e escrever automático deixaria dois contatos vivos
                // com o mesmo número. A faixa é, sempre, um pedido de ação.
                case 'CONTATO_ALTEROU_NUMERO': {
                    const de = meta.numeroAnterior;
                    const para = meta.numeroNovo;
                    if (!de || !para) return 'O contato informou a troca do número de telefone';

                    // DESTINO: esta conversa nasceu sob o número NOVO — na API
                    // Oficial, um segundo cadastro, porque a Cloud API não
                    // entrega LID e nada o reuniu ao contato antigo.
                    if (meta.perspectiva === 'DESTINO') {
                        return `Este número foi informado como o novo número de ${de} — o cadastro daquele contato não foi alterado`;
                    }

                    return `O contato informou a troca do número ${de} para ${para} — o cadastro não foi alterado`;
                }
                // Mesma redação do servidor (`montarConteudoEvento`): ele congela
                // uma cópia em `conteudo` para exportação e relatório, e duas
                // redações divergentes seriam duas verdades sobre a ligação.
                case 'LIGACAO': {
                    const duracao = formatarDuracaoFalada(meta.duracaoSegundos);
                    const tipoLigacao = meta.ehVideo ? 'Chamada de vídeo' : 'Ligação';

                    if (meta.direcao === 'REALIZADA') {
                        if (duracao) return ator ? `${ator} ligou para o contato · ${duracao}` : `${tipoLigacao} realizada · ${duracao}`;
                        if (meta.situacao === 'RECUSADA') return ator ? `${ator} ligou para o contato — recusada` : `${tipoLigacao} realizada — recusada`;
                        return ator ? `${ator} ligou para o contato — sem resposta` : `${tipoLigacao} realizada — sem resposta`;
                    }

                    if (duracao) return ator ? `${ator} atendeu uma ligação · ${duracao}` : `${tipoLigacao} recebida · ${duracao}`;
                    // Atendida no CELULAR pareado: alguém falou com o cliente,
                    // só não pelo whiz. Chamar isso de "perdida" apaga o
                    // atendimento — e o número da inbox costuma seguir instalado
                    // num telefone que toca junto.
                    if (meta.motivo === 'accepted_elsewhere') return `${tipoLigacao} recebida atendida em outro dispositivo`;
                    if (meta.situacao === 'RECUSADA') return ator ? `${ator} recusou uma ligação` : `${tipoLigacao} recebida recusada`;
                    // PERDIDA é o timeout (tocou até o fim); ENCERRADA sem
                    // duração é o cliente desistindo antes.
                    if (meta.situacao === 'PERDIDA') return `${tipoLigacao} recebida perdida`;
                    return `${tipoLigacao} recebida não atendida`;
                }

                case 'RODIZIO':
                    return fraseRodizio(meta);

                // Mesma redação do servidor (`montarBotFalhou`). O motivo vai inteiro,
                // com o código da Meta: é a resposta para "por que o contato ficou sem
                // resposta?", e uma versão amigável perderia justamente isso.
                case 'BOT_FALHOU': {
                    const motivo = String(meta.motivo ?? '').trim().replace(/\.$/, '') || 'motivo não informado';
                    const codigo = meta.codigoErro ? ` (código ${meta.codigoErro})` : '';
                    const desfecho = meta.fluxoInterrompido === false ? '' : '. O bot foi desvinculado do atendimento';
                    if (meta.causa === 'SEM_CANAIS') return `Falha na Lista de Canais do bot: ${motivo}${desfecho}`;
                    if (meta.causa === 'ENVIO_RECUSADO') return `A Lista de Canais do bot não foi enviada${codigo}: ${motivo}${desfecho}`;
                    if (meta.causa === 'ENTREGA_RECUSADA') return `A Meta recusou a Lista de Canais do bot depois de aceitá-la${codigo}: ${motivo}${desfecho}`;
                    if (meta.nomeTemplate) return `O template "${meta.nomeTemplate}" do bot não foi enviado: ${motivo}${desfecho}`;
                    return `O bot falhou${meta.acao ? ` na ação ${meta.acao}` : ''}: ${motivo}${desfecho}`;
                }

                default:
                    return props.message.text ?? 'Evento do atendimento';
            }
        });

        /**
         * Ligação que não teve fala: perdida, recusada, ou encerrada antes de
         * alguém atender. `duracaoSegundos` nulo é o sinal — zero de verdade
         * (atendeu e desligou no mesmo segundo) continua sendo fala.
         */
        const ligacaoSemFala = computed(() =>
            props.message.noteTipo === 'LIGACAO'
            && (props.message.noteMetadados?.duracaoSegundos ?? null) === null
            // Atendida em outro dispositivo teve fala (no celular), então o
            // ícone de telefone cortado seria errado.
            && props.message.noteMetadados?.motivo !== 'accepted_elsewhere',
        );

        /**
         * A anotação de campanha é o único evento clicável: ela leva ao envio que a
         * originou. Exige `idDisparo` — sem ele não há para onde ir, e um card que
         * não faz nada ao ser clicado é pior que um card estático.
         *
         * Sem gate de permissão: o modal de Campanhas não tem `v-permission` em
         * nenhum dos pontos de entrada, e a contenção real é do servidor
         * (`GET /disparos` filtra pelos inboxes do token). Campanha fora do alcance
         * cai no aviso do próprio modal.
         */
        const ehCampanhaClicavel = computed(() =>
            props.message.noteTipo === 'DISPARO_ENVIADO'
            && !!props.message.noteMetadados?.idDisparo
            // O embed renderiza o chat sem o modal de Campanhas: lá a anotação fica
            // informativa em vez de virar um botão que não abre nada.
            && uiStore.campanhasModalMontado,
        );

        const abrirCampanha = () => {
            const meta = props.message.noteMetadados;
            if (!meta?.idDisparo) return;
            uiStore.openCampanhasModal({ idDisparo: meta.idDisparo, idAgendamento: meta.idAgendamento ?? null });
        };

        // Mesmo formatador das mensagens: hora nua hoje, `dd/mm/aaaa às HH:MM` fora.
        // Formatar diferente deixaria a nota antiga com hora solta ao lado de
        // mensagens datadas, no mesmo grupo de dia.
        const horaFormatada = computed(() => FormatTime(props.message.time));

        // "Você deixou..." para o autor, como nos avisos de grupo do WhatsApp.
        const autorExibido = computed(() => (isAuthor.value ? 'Você' : props.message.noteAuthorName || 'Atendente'));

        const onEdit = (hide: () => void) => {
            hide();
            chatStore.abrirModalAnotacaoInterna({ id: props.message.id, conteudo: props.message.text ?? '' });
        };

        const onDelete = async (hide: () => void) => {
            hide();
            const confirmado = await AreYouSureAboutThat(
                'A anotação será removida para toda a equipe.',
                'Excluir anotação',
                'Sim, excluir',
                'Cancelar',
            );
            if (!confirmado) return;
            await chatStore.removerAnotacaoInterna(props.message.id);
        };

        return { isAuthor, isEvento, icone, fraseEvento, autorExibido, horaFormatada, onEdit, onDelete, ehCampanhaClicavel, abrirCampanha };
    },
});
</script>

<style scoped>
/* Faixa larga centralizada, no lugar de bolha: a anotação é um aviso do sistema
   dirigido à equipe inteira, não a fala de um dos lados da conversa — mesma
   linguagem visual dos avisos de grupo do WhatsApp.

   O evento de sistema usa a mesma faixa em cinza: mesma família, hierarquia
   menor. O âmbar fica reservado ao que alguém escreveu à mão. */
.internal-note-wrapper {
    display: flex;
    justify-content: center;
    margin: 14px 0;
    list-style: none;
}

.internal-note-banner {
    position: relative;
    width: 100%;
    max-width: 68%;
    background: rgba(var(--bs-warning-rgb), 0.10);
    border: 1px solid rgba(var(--bs-warning-rgb), 0.28);
    border-radius: 8px;
    padding: 8px 34px;
    text-align: center;
}

/* Sempre mais estreito que a anotação manual: o evento é um aviso de sistema,
   não conteúdo escrito por alguém. Mexer na largura de um exige mexer no outro
   para a hierarquia não sumir. */
.internal-note-banner.is-evento {
    max-width: 58%;
    background: var(--bs-secondary-bg);
    border-color: var(--bs-border-color);
    padding: 6px 14px;
}

/* Evento não tem corpo separado: a frase É o conteúdo, então o lead perde o
   tom âmbar e some a linha de texto. */
.internal-note-banner.is-evento .internal-note-lead {
    margin: 0 0 2px;
    font-size: 12.5px;
    color: var(--bs-body-color);
}

/* ── Fatos do Agente de IA ────────────────────────────────────────────────────
   Roxo é a cor da implementação do bot-ia no produto — a mesma dos selos de
   Agente. A timeline mistura eventos do whiz (cinza) e do Agente, e sem uma cor
   própria o operador não consegue separar de relance o que o SISTEMA fez do que
   o AGENTE fez, que é justamente a pergunta que ele traz para esta tela.

   Só a borda esquerda e o ícone recebem a cor: pintar o card inteiro colocaria
   um aviso de sistema acima da conversa na hierarquia visual. */
.internal-note-banner.is-evento.is-agente-ia {
    border-left: 3px solid var(--bs-purple, #6f42c1);
    background: color-mix(in srgb, var(--bs-purple, #6f42c1) 7%, var(--bs-secondary-bg));
}

.internal-note-banner.is-evento.is-agente-ia .internal-note-lead > i {
    color: var(--bs-purple, #6f42c1);
}

/* ── Falha do bot ──────────────────────────────────────────────────────────────
   Vermelho: é a única faixa que pede ação — o contato ficou sem resposta e, em
   geral, o bot saiu do atendimento. Mesma receita do Agente (borda + ícone + fundo
   leve), para não colocar o aviso acima da conversa na hierarquia. */
.internal-note-banner.is-evento.is-falha {
    border-color: rgba(var(--bs-danger-rgb), 0.35);
    border-left: 3px solid var(--bs-danger);
    background: rgba(var(--bs-danger-rgb), 0.07);
}

.internal-note-banner.is-evento.is-falha .internal-note-lead {
    color: var(--bs-danger-text-emphasis);
}

.internal-note-banner.is-evento.is-falha .internal-note-lead > i {
    color: var(--bs-danger);
}

/* A anotação de campanha é a única clicável: ela leva ao envio que a originou.
   Herda a faixa cinza do evento e ganha só os sinais de que dá para clicar —
   cursor, realce na borda esquerda e a chamada. Virar card colorido a colocaria
   acima da conversa na hierarquia, e ela é moldura, não conteúdo. */
.internal-note-banner.is-campanha {
    cursor: pointer;
    border-left: 3px solid var(--bs-primary);
    transition: background-color 0.15s ease-in-out, border-color 0.15s ease-in-out;
}

.internal-note-banner.is-campanha:hover,
.internal-note-banner.is-campanha:focus-visible {
    background: rgba(var(--bs-primary-rgb), 0.08);
    border-color: var(--bs-primary);
}

.internal-note-banner.is-campanha:focus-visible {
    outline: 2px solid var(--bs-primary);
    outline-offset: 2px;
}

.internal-note-banner.is-campanha .internal-note-lead i {
    color: var(--bs-primary);
}

/* Chamada discreta: some no repouso e só ganha cor no hover, para a faixa não
   competir com as mensagens em volta enquanto ninguém está mirando nela. */
.internal-note-cta {
    margin: 2px 0 4px;
    font-size: 11.5px;
    font-weight: 600;
    color: var(--bs-secondary-color);
    transition: color 0.15s ease-in-out;
}

.internal-note-banner.is-campanha:hover .internal-note-cta,
.internal-note-banner.is-campanha:focus-visible .internal-note-cta {
    color: var(--bs-primary);
}

.internal-note-cta i {
    margin-left: 2px;
}

.internal-note-menu-btn {
    background: transparent;
    border: 0;
    padding: 0 2px;
    line-height: 1;
    color: var(--bs-secondary-color);
    cursor: pointer;
}

/* Ancorado na faixa e revelado no hover/foco, como as ações de mensagem: a
   faixa é lida como aviso, o botão fixo a transformaria em card de ação. */
.internal-note-actions {
    position: absolute;
    top: 6px;
    right: 8px;
    opacity: 0;
    transition: opacity 0.15s ease-in-out;
}

.internal-note-banner:hover .internal-note-actions,
.internal-note-actions:focus-within,
.internal-note-actions.v-popper--shown {
    opacity: 1;
}

/* Sem hover em toque, o menu ficaria inalcançável. */
@media (hover: none) {
    .internal-note-actions {
        opacity: 1;
    }
}

.internal-note-menu-btn:hover {
    color: var(--bs-body-color);
}

.internal-note-menu {
    display: flex;
    flex-direction: column;
    min-width: 140px;
    padding: 4px;
}

.internal-note-menu-item {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: 0;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 13px;
    text-align: left;
    color: var(--bs-body-color);
    cursor: pointer;
}

.internal-note-menu-item:hover {
    background: var(--bs-light);
}

.internal-note-menu-item.danger {
    color: var(--bs-danger);
}

/* Frase de abertura no lugar do rótulo em caixa alta: quem leu já sabe o que é
   sem decodificar badge, e a autoria deixa de repetir no rodapé. */
.internal-note-lead {
    margin: 0 0 4px;
    font-size: 12px;
    color: var(--bs-warning-text-emphasis);
}

.internal-note-lead i {
    margin-right: 4px;
}

/* Maior que o corpo das mensagens (14.5px): a anotação é o conteúdo da faixa,
   a frase de abertura e o rodapé são moldura. */
.internal-note-text {
    margin: 2px 0 6px;
    font-size: 15px;
    line-height: 1.4;
    white-space: pre-wrap;
    word-break: break-word;
    color: var(--bs-body-color);
}

/* Hora e visibilidade numa linha só, quebrando quando não couber. */
.internal-note-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 4px;
    font-size: 11px;
    color: var(--bs-secondary-color);
}

.internal-note-author {
    font-weight: 600;
}

.internal-note-sep {
    opacity: 0.6;
}

.internal-note-edited {
    font-style: italic;
}

.internal-note-hint {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    opacity: 0.85;
}

/* O âmbar do tema claro perde contraste sobre fundo escuro: no dark a faixa
   carrega o realce na borda e num fundo translúcido, não numa área clara. */
[data-bs-theme="dark"] .internal-note-banner {
    background: rgba(var(--bs-warning-rgb), 0.07);
    border-color: rgba(var(--bs-warning-rgb), 0.22);
}

[data-bs-theme="dark"] .internal-note-lead {
    color: var(--bs-warning);
}

[data-bs-theme="dark"] .internal-note-banner.is-campanha:hover,
[data-bs-theme="dark"] .internal-note-banner.is-campanha:focus-visible {
    background: rgba(var(--bs-primary-rgb), 0.14);
}

[data-bs-theme="dark"] .internal-note-menu {
    background: var(--bs-secondary-bg);
}

[data-bs-theme="dark"] .internal-note-menu-item:hover {
    background: rgba(255, 255, 255, 0.06);
}
</style>